import { Button, ButtonColors, LoadingPage } from '@chiroup/components';
import { Appointment, HealthComLocation, convertToMS } from '@chiroup/core';
import { Dialog } from '@headlessui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';
import appointmentService from '../../services/appointment.service';
import locationService from '../../services/location.service';
import ScheduleTime from '../ScheduleTime';
import Modal from '../common/Modal';

type Props = {
  updateAppointment: (id: string, data: Partial<Appointment>) => void;
  provider: string;
  treatment: number;
  id: string;
  location: HealthComLocation;
  primaryColor: string;
  startTime: string | number;
  startTimeFormatted?: string;
  cancel: () => void;
  path: string;
};

const ChangeAppointmentButton: React.FC<Props> = ({
  updateAppointment,
  provider,
  treatment,
  id,
  location,
  primaryColor,
  startTime,
  startTimeFormatted,
  cancel,
  path,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [viewDate, setViewDate] = useState(
    dayjs(convertToMS(startTime)).startOf('month'),
  );
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availability, setAvailability] = useState<{
    [key: string]: {
      [key: string]: {
        date: string;
        startTime: Dayjs;
        endTime: Dayjs;
        provider: string;
        duration: number;
      }[];
    };
  }>();
  const [dottedDays, setDottedDays] = useState<{
    [key: number]: boolean;
  }>({});
  const [availableTimes, setAvailableTimes] = useState<
    {
      providerId: string;
      providerName?: string;
      profileImage?: string | null;
      times: {
        date: string;
        startTime: Dayjs;
        endTime: Dayjs;
        provider: string;
        duration: number;
      }[];
    }[]
  >([]);
  const [selectedDay, setSelectedDay] = useState<string>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setAvailableTimes([]);
    setError(null);
    setIsOpen(false);
  };

  const providerInfo = useCallback(
    (userId: string) => {
      return location?.providers?.find((p) => p.ID === userId);
    },
    [location?.providers],
  );

  const onSelectDate = useCallback(
    (
      date: string,
      avail:
        | {
            [key: string]: {
              [key: string]: {
                date: string;
                startTime: Dayjs;
                endTime: Dayjs;
                provider: string;
                duration: number;
              }[];
            };
          }
        | undefined = availability,
    ) => {
      if (!date) return setAvailableTimes([]);
      const availabilityForDay = avail?.[date];
      const availabilityForProvider = [
        {
          providerId: provider,
          providerName: providerInfo(provider)?.name,
          profileImage: providerInfo(provider)?.profileImage,
          times: availabilityForDay?.[provider] || [],
        },
      ];
      setAvailableTimes(availabilityForProvider);
      setSelectedDay(dayjs(date).format('MMMM D, YYYY'));
    },
    [provider, providerInfo, availability],
  );

  const getAvailability = async (
    provider: string | null,
    treatment: number | null,
    year: string,
    month: string,
  ) => {
    setLoadingAvailability(true);
    const res = await locationService.getAvailability({
      path,
      provider,
      treatment,
      year,
      month,
      timezone: location.timezone,
    });
    setAvailability(res);
    setDottedDays(
      Object.entries(res).reduce(
        (
          acc: {
            [key: number]: boolean;
          },
          [date, dayAvailability],
        ) => {
          const day = dayjs(date).date();
          const isAvailable = Object.values(dayAvailability).some(
            (times) => times.length,
          );
          if (isAvailable) {
            acc[day] = true;
          }
          return acc;
        },
        {},
      ),
    );
    setLoadingAvailability(false);
    return res;
  };

  const openModal = async () => {
    setIsOpening(true);
    const startTimeDayjs = dayjs(convertToMS(startTime));
    setSelectedDate(startTimeDayjs);
    const res = await getAvailability(
      provider,
      treatment,
      viewDate.format('YYYY'),
      viewDate.format('MM'),
    );
    setIsOpening(false);
    setIsOpen(true);
    onSelectDate(startTimeDayjs.format('YYYY-MM-DD'), res);
  };

  const closeAndCancel = () => {
    close();
    cancel();
  };

  const reschedule = async (val: {
    date: string;
    startTime: Dayjs;
    endTime: Dayjs;
    provider: string;
    duration: number;
  }) => {
    try {
      setError(null);
      setIsSaving(true);
      const res = await appointmentService.update(id, val);
      setIsSaving(false);
      close();
      updateAppointment(id, res);
    } catch (err: any) {
      setIsSaving(false);
      setError(err?.response?.data?.message || 'Something went wrong.');
    }
  };

  return (
    <>
      <Button
        color={ButtonColors.plainWithBorder}
        onClick={openModal}
        loading={isOpening}
        text="Change"
      />
      <Modal isOpen={isOpen} close={close}>
        {isSaving ? (
          <LoadingPage />
        ) : (
          <ScheduleTime
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onChange={reschedule}
            primaryColor={primaryColor}
            onSelectDate={onSelectDate}
            selectedDay={selectedDay}
            onChangeMonth={(year: string, month: string) => {
              getAvailability(provider, treatment, year, month);
            }}
            dottedDays={dottedDays}
            loading={loadingAvailability}
            availableTimes={availableTimes}
            setViewDate={setViewDate}
            viewDate={viewDate}
            title={
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <CalendarIcon
                    className="h-6 w-6 text-gray-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <Dialog.Title
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    Change Appointment
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      To reschedule your {startTimeFormatted} appointment with
                      the same provider and treatment, select a new date and
                      time below. Otherwise,{' '}
                      <span
                        className="text-primary hover:opacity-70 hover:underline cursor-pointer"
                        onClick={closeAndCancel}
                      >
                        cancel
                      </span>{' '}
                      the existing appointment and book a new one.
                    </p>
                  </div>
                </div>
              </div>
            }
            error={error}
            compact
          />
        )}
      </Modal>
    </>
  );
};

export default ChangeAppointmentButton;
